/**
 * VendorAlertSettings: the one "Alert settings" panel every vendor type mounts
 * on its Profile / Notifications screen (restaurant, shop, brand, healthcare,
 * event organizer). Backed by the unified vendor-alerts API:
 *
 *   GET  /api/vendor-alerts/me        load prefs, resolved state, providers, recent
 *   PUT  /api/vendor-alerts/me        save channels, contacts, events
 *   POST /api/vendor-alerts/me/test   fire a test alert on every enabled channel
 *
 * A `null` pref means "inherit" (the legacy profile toggle or the platform
 * default). Every switch renders the RESOLVED value so what the vendor sees is
 * what the dispatcher will do, and a small "inherited" chip marks the prefs the
 * vendor has not set explicitly.
 *
 * Drop-in: `<VendorAlertSettings />` loads, saves and tests on its own.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Switch } from "@material-ui/core";
import { toast } from "react-toastify";
import {
  apiGetVendorAlerts,
  apiUpdateVendorAlerts,
  apiSendVendorTestAlert,
} from "../api/index";
import { WHATSAPP_SENDER } from "../config/platformContacts";
import { useGate } from "./team/Gated";
// What each switch does on the server: views/Settings/help/vendorSettingsHelp.js.
import SettingHelp from "../views/Settings/settingsHelp";
import "./VendorAlertSettings.css";

const CHANNELS = [
  {
    key: "email",
    label: "Email",
    icon: "fas fa-envelope",
    color: "#0a578b",
    tint: "#f0f7ff",
    help: null,
    desc: "An email to your alert email for each alert switched on below.",
  },
  {
    key: "sms",
    label: "SMS",
    icon: "fas fa-sms",
    color: "#057a55",
    tint: "#f0fdf4",
    help: null,
    desc: "A short text message to your alert phone for each alert switched on below.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: "fab fa-whatsapp",
    color: "#128c7e",
    tint: "#effcf6",
    help: <SettingHelp topic="vendorAlertWhatsapp" />,
    desc: "A WhatsApp message from Lusuku's number to your alert phone, with the order details.",
  },
];

const EVENTS = [
  {
    key: "newOrder",
    label: "New orders",
    icon: "fas fa-shopping-bag",
    color: "#f6961e",
    tint: "#fff7ed",
    help: <SettingHelp topic="vendorAlertNewOrder" />,
    desc: "Alert me when an order is placed with my business or one of my branches. Walk-in POS sales are not alerted.",
  },
  {
    key: "profileChanged",
    label: "Changes to my profile",
    icon: "fas fa-user-edit",
    color: "#7e3af2",
    tint: "#faf5ff",
    help: <SettingHelp topic="vendorAlertProfileChanged" />,
    desc: "Alert me when my name, contacts, address or hours are edited, including by Lusuku staff. Switching this off also silences the warning sent when the alert email or phone is changed.",
  },
];

/**
 * Why an event switch is disabled.
 *
 * The server says which events can actually fire for this vendor type
 * (`supportedEvents`); this is the sentence the vendor reads underneath the one
 * that cannot. An organizer's "New orders" toggle used to look identical to a
 * live one, so it read as a promise that a ticket sale would reach them.
 */
const unsupportedEventReason = (vendorType, key) => {
  if (key === "newOrder" && vendorType === "EVENT_ORGANIZER") {
    return "Ticket sale alerts are not wired yet; profile change alerts are.";
  }
  return "This alert is not wired for your account type yet.";
};

/**
 * Row labels for "Recent alerts", and for nothing else.
 *
 * Those rows are VendorAlertLog documents (recentRows in
 * controllers/vendorAlertController.js) and that schema's `event` is the enum
 * NEW_ORDER | PROFILE_CHANGED | TEST, so the enum is the only shape that ever
 * reaches this map. The OTHER path, the switches higher up the screen, uses the
 * `alertPrefs.events` keys (newOrder, profileChanged) and carries its own text
 * in EVENTS, so the camelCase entries that used to sit here could not be hit
 * from either path and are gone.
 */
const EVENT_LABELS = {
  NEW_ORDER: "New order",
  PROFILE_CHANGED: "Profile changed",
  TEST: "Test alert",
};

const CHANNEL_LABELS = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };

/**
 * The dispatcher records machine tags, not sentences, and both lists on this
 * screen used to print them raw ("no_email_address"). This is the vendor's own
 * diagnostic screen, so the tags it can write become English here. Anything
 * unmapped is shown exactly as the server wrote it rather than swallowed, so a
 * new tag is still readable the day it appears.
 */
const REASON_TEXT = {
  channel_off: "You have this channel switched off.",
  event_off: "You have this alert switched off.",
  no_email_address: "There is no email address to send this to.",
  no_phone_number: "There is no phone number to send this to.",
  email_not_configured: "The email provider is not configured on the server.",
  twilio_not_configured: "The SMS provider is not configured on the server.",
  twilio_whatsapp_not_configured:
    "The WhatsApp fallback sender is not configured on the server.",
  send_failed: "The provider did not accept the message.",
  TARGET_CAP_EXCEEDED: "That order had more sellers on it than one alert run can cover.",
};

const humanReason = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const key = text.replace(/\.$/, "");
  if (REASON_TEXT[key]) return REASON_TEXT[key];
  // Reasons compose. The dispatcher prefixes a row's own reason with the
  // caller's tag ("contacts_moved_current: no_phone_number"), and the test
  // endpoint's per-channel `summary` reads "SMS skipped: twilio_not_configured."
  // Translate the tail when it is one we know, keep whatever came before it.
  const cut = key.lastIndexOf(":");
  const tail = cut > -1 ? key.slice(cut + 1).trim() : "";
  if (tail && REASON_TEXT[tail]) return `${key.slice(0, cut).trim()}: ${REASON_TEXT[tail]}`;
  return text;
};

/**
 * `provider` is an enum and it is the STRING "NONE" whenever a channel never
 * reached a sender, which is exactly when it is skipped. "NONE" is truthy, so
 * every skipped channel used to read "Email via NONE". Show a sender only when
 * there is a real one, and name it the way a person would.
 */
const PROVIDER_LABELS = {
  SMTP: "SMTP",
  SENDGRID: "SendGrid",
  TWILIO_SMS: "Twilio",
  TWILIO_WHATSAPP: "Twilio",
  META_WHATSAPP: "WhatsApp Cloud API",
};

const providerLabel = (value) => {
  const key = String(value || "").trim();
  if (!key || key === "NONE") return "";
  return PROVIDER_LABELS[key] || key;
};

const STATUS = {
  SENT: { label: "Sent", color: "#15803d", bg: "#ecfdf5", icon: "fas fa-check-circle" },
  SKIPPED: { label: "Skipped", color: "#b45309", bg: "#fffbeb", icon: "fas fa-minus-circle" },
  FAILED: { label: "Failed", color: "#b91c1c", bg: "#fef2f2", icon: "fas fa-times-circle" },
};

/*
 * CONTACT VALIDATION. The server is the judge; this check only spares the
 * vendor a round trip, so it must accept EXACTLY what the server accepts and
 * never less. It used to be /^\+?[1-9]\d{7,14}$/, which rejects a leading zero,
 * so "0772000111" (the normal way to write a number here) was blocked with
 * "Use the international format" while the server would have taken it happily.
 *
 * services/vendorAlerts/vendorAlertPrefs.js toE164(phone, country), verbatim:
 *
 *   if (!phone) return null;
 *   const p = String(phone).replace(/[^\d+]/g, "");
 *   if (!p) return null;
 *   if (p.startsWith("+")) return p.length >= 11 ? p : null;
 *   let dial;
 *   try { dial = String(getDialCode(country) || "256"); } catch { dial = "256"; }
 *   if (p.startsWith(dial)) return `+${p}`;
 *   if (p.startsWith("0")) return `+${dial}${p.slice(1)}`;
 *   if (p.length === 9) return `+${dial}${p}`;
 *   return null;
 *
 * and the email rule, isUsableAlertEmail() in the same file:
 *
 *   typeof value === "string" && value.trim().length <= 120 && EMAIL_RE.test(value.trim())
 *
 * with EMAIL_RE identical to the one below. The 120-character cap was the one
 * server rule this file did not have, so an over-long address passed here and
 * was rejected there.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_MAX = 120;

// Both messages are the server's own words, so a value rejected here and the
// same value rejected there read the same.
const EMAIL_INVALID =
  "Enter a valid alert email address, or clear the field to use your account email.";
const PHONE_INVALID =
  "Enter a valid alert phone number in local or international form (for example 0772000111 or +256772000111).";
const PHONE_HINT = "Use local or international form, for example 0772000111 or +256772000111.";

const looksLikeAlertEmail = (raw) => {
  const value = String(raw || "").trim();
  return value.length > 0 && value.length <= EMAIL_MAX && EMAIL_RE.test(value);
};

const looksLikeAlertPhone = (raw) => {
  const p = String(raw || "").replace(/[^\d+]/g, "");
  if (!p) return false;
  if (p.startsWith("+")) return p.length >= 11;
  if (p.startsWith("0")) return true;
  if (p.length === 9) return true;
  // The one branch left is `p.startsWith(dial)`, and no response this screen
  // receives carries the vendor document's country, so the dial code is not
  // knowable here. Any other run of digits therefore goes to the server rather
  // than being blocked on a guess: the server answers 400 with its message and
  // that message now lands on this input (see fieldLeaf below).
  return /^\d+$/.test(p);
};

/**
 * FIELD BINDING. The server names a rejected field by its DOTTED path:
 * "contacts.alertEmail", "contacts.alertPhone", "channels.email",
 * "events.newOrder" (sanitizeVendorAlertPrefsInput, passed straight through by
 * vendorAlertController). The inputs here are keyed by the bare leaf, so the
 * old `fieldError.field === "alertPhone"` never once matched: a rejected value
 * got a toast and no red border, and the phone fallback message below it was
 * dead code. Compare on the leaf, which matches the dotted path and a bare name
 * both, so this cannot break if the server ever changes shape.
 */
const fieldLeaf = (field) => {
  const parts = String(field || "").split(".");
  return parts[parts.length - 1] || "";
};

const isFieldError = (fieldError, key) => !!key && fieldLeaf(fieldError?.field) === key;

// Some hosts (BrandOwnerSettings) render this panel inside their own <form>.
// Enter in a contact input must not submit that outer form.
const swallowEnter = (e) => {
  if (e.key === "Enter") e.preventDefault();
};

const emptyPrefs = () => ({
  channels: { email: null, sms: null, whatsapp: null },
  contacts: { alertEmail: null, alertPhone: null },
  events: { newOrder: null, profileChanged: null },
});

const prefsFrom = (data) => {
  const p = data?.prefs || {};
  const base = emptyPrefs();
  return {
    channels: { ...base.channels, ...(p.channels || {}) },
    contacts: { ...base.contacts, ...(p.contacts || {}) },
    events: { ...base.events, ...(p.events || {}) },
  };
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_e) {
    return "";
  }
};

const Y = {
  head: { fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#90a0b7", marginBottom: 4 },
  sub: { fontSize: 13, color: "#6b7a99", margin: "0 0 14px" },
  iconBox: (on, color) => ({
    width: 34, height: 34, borderRadius: 8, flexShrink: 0,
    background: on ? color : "#f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "center",
  }),
  icon: (on) => ({ fontSize: "0.9rem", color: on ? "#fff" : "#94a3b8" }),
  title: { fontWeight: 700, fontSize: "0.83rem", color: "#1e293b" },
  desc: { fontSize: "0.75rem", color: "#64748b" },
  chip: (color) => ({
    fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase",
    padding: "1px 7px", borderRadius: 20, letterSpacing: "0.5px",
    background: `${color}18`, color,
  }),
  provider: (ok) => ({
    fontSize: "0.72rem", marginTop: 4, display: "flex", alignItems: "center", gap: 6,
    color: ok ? "#15803d" : "#b45309",
  }),
  label: { fontSize: "0.72rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5, display: "flex", alignItems: "center", gap: 8 },
  help: { fontSize: "0.74rem", color: "#94a3b8", marginTop: 5 },
  err: { fontSize: "0.74rem", color: "#dc2626", marginTop: 5, fontWeight: 600 },
  badge: (s) => ({
    display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
    fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
    padding: "2px 8px", borderRadius: 20, background: s.bg, color: s.color,
  }),
  muted: { fontSize: "0.8rem", color: "#94a3b8", padding: "12px 16px" },
  notice: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", fontSize: "0.85rem", color: "#475569", lineHeight: 1.5 },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.SKIPPED;
  return (
    <span style={Y.badge(s)}>
      <i className={s.icon} />
      {s.label}
    </span>
  );
};

const SectionHead = ({ icon, color, title, sub }) => (
  <div className="vas-section-head">
    <i className={icon} style={{ color, fontSize: "0.85rem", width: 16, textAlign: "center" }} />
    <span className="vas-section-title">{title}</span>
    {sub && <span className="vas-section-sub">{sub}</span>}
  </div>
);

export default function VendorAlertSettings() {
  // Vendor team permissions: saving or testing alert preferences is
  // settings:edit (the PUT and the test route both require it).
  const settingsGate = useGate("settings:edit");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(emptyPrefs());
  const [loading, setLoading] = useState(true);
  const [noVendor, setNoVendor] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const applyResponse = useCallback((payload) => {
    setData(payload || null);
    setDraft(prefsFrom(payload));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setNoVendor(false);
    try {
      const { data: payload } = await apiGetVendorAlerts();
      applyResponse(payload);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 404 && code === "VENDOR_ALERT_NO_VENDOR") {
        setNoVendor(true);
      } else {
        setLoadError(err?.response?.data?.message || err?.response?.data?.error || "Could not load alert settings.");
      }
    } finally {
      setLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    load();
  }, [load]);

  const resolved = data?.resolved || {};
  const providers = data?.providers || {};
  const recent = Array.isArray(data?.recent) ? data.recent : [];
  // Older servers do not send `supportedEvents`. Absent means "no opinion", so
  // every event renders as it always did rather than every switch going dead.
  const supportedEvents = Array.isArray(data?.supportedEvents)
    ? data.supportedEvents
    : EVENTS.map((ev) => ev.key);

  const dirty = data ? JSON.stringify(draft) !== JSON.stringify(prefsFrom(data)) : false;

  const effective = (group, key) => {
    const v = draft?.[group]?.[key];
    if (v === null || v === undefined) return !!resolved?.[group]?.[key];
    return !!v;
  };

  const setPref = (group, key, value) =>
    setDraft((d) => ({ ...d, [group]: { ...(d?.[group] || {}), [key]: value } }));

  const setContact = (key, value) => {
    setFieldError((fe) => (isFieldError(fe, key) ? null : fe));
    setPref("contacts", key, value);
  };

  const providerState = (key) => {
    if (key === "email") return { ok: !!providers.email, via: providers.email ? "Email provider ready" : "Email provider not configured on the server, this channel will be skipped" };
    if (key === "sms") return { ok: !!providers.sms, via: providers.sms ? "SMS provider ready" : "SMS provider not configured on the server, this channel will be skipped" };
    const platform = !!providers.whatsappPlatform;
    const twilio = !!providers.whatsappTwilio;
    if (platform) return { ok: true, via: `WhatsApp ready. Messages arrive from ${WHATSAPP_SENDER}` };
    if (twilio) return { ok: true, via: "WhatsApp ready (Twilio sender)" };
    return { ok: false, via: "WhatsApp provider not configured on the server, this channel will be skipped" };
  };

  const validateContacts = () => {
    const email = draft?.contacts?.alertEmail;
    if (email && !looksLikeAlertEmail(email)) {
      // Dotted, exactly as the server would name it, so both shapes go through
      // the same leaf comparison on the way to the input.
      return { field: "contacts.alertEmail", message: EMAIL_INVALID };
    }
    const phone = draft?.contacts?.alertPhone;
    if (phone && !looksLikeAlertPhone(phone)) {
      return { field: "contacts.alertPhone", message: PHONE_INVALID };
    }
    return null;
  };

  const save = async () => {
    const problem = validateContacts();
    if (problem) {
      setFieldError(problem);
      toast.error(problem.message);
      return;
    }
    setSaving(true);
    setFieldError(null);
    try {
      const body = {
        channels: draft.channels,
        contacts: {
          alertEmail: draft.contacts.alertEmail ? String(draft.contacts.alertEmail).trim() : null,
          alertPhone: draft.contacts.alertPhone ? String(draft.contacts.alertPhone).trim() : null,
        },
        events: draft.events,
      };
      const { data: payload } = await apiUpdateVendorAlerts(body);
      applyResponse(payload);
      toast.success("Alert settings saved.");
    } catch (err) {
      const res = err?.response;
      if (res?.status === 400 && res?.data?.code === "VENDOR_ALERT_INVALID") {
        const field = res.data.field;
        const leaf = fieldLeaf(field);
        const fallback =
          leaf === "alertPhone"
            ? PHONE_INVALID
            : leaf === "alertEmail"
              ? EMAIL_INVALID
              : "Please check this value.";
        const message = res.data.message || fallback;
        // `field` is stored as the server sent it; every comparison goes
        // through fieldLeaf, so the dotted path binds to the right input.
        setFieldError({ field, message });
        toast.error(message);
      } else if (res?.status === 404 && res?.data?.code === "VENDOR_ALERT_NO_VENDOR") {
        setNoVendor(true);
      } else {
        toast.error(res?.data?.message || res?.data?.error || "Could not save alert settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const { data: payload } = await apiSendVendorTestAlert();
      // The server answers with results KEYED BY CHANNEL
      // ({ email: {...}, sms: {...}, whatsapp: {...} }), and each entry carries
      // no `channel` of its own. Flatten to the array this list renders, adding
      // the key back as `channel`. An array is still accepted in case the
      // endpoint shape ever changes.
      const raw = payload?.results;
      const results = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? Object.keys(CHANNEL_LABELS)
              .filter((key) => raw[key])
              .map((key) => ({ channel: key, ...raw[key] }))
          : [];
      setTestResults(results);
      const sent = results.filter((r) => r.status === "SENT").length;
      if (sent > 0) toast.success(`Test alert sent on ${sent} channel${sent === 1 ? "" : "s"}.`);
      else toast.warn("No channel delivered the test. See the results below.");
    } catch (err) {
      const res = err?.response;
      if (res?.status === 429) {
        // The limiter is a TEN minute window and says so in its own body
        // (routes/vendorAlertRoutes.js). The old copy here said "a few minutes"
        // and threw the server's sentence away.
        toast.warn(res?.data?.message || "Too many test alerts. Wait ten minutes and try again.");
      } else if (res?.status === 404 && res?.data?.code === "VENDOR_ALERT_NO_VENDOR") {
        setNoVendor(true);
      } else {
        toast.error(res?.data?.message || res?.data?.error || "Could not send the test alert.");
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: "20px 0", color: "#6b7a99" }}>Loading alert settings...</div>;
  }

  if (noVendor) {
    return (
      <div className="vas-wrap">
        <div style={Y.head}>Alert settings</div>
        <div style={Y.notice}>
          <i className="fas fa-info-circle" style={{ color: "#0a578b", marginRight: 8 }} />
          Alert settings become available once your vendor profile has been created and approved.
          Nothing to configure here yet.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="vas-wrap">
        <div style={Y.head}>Alert settings</div>
        <div style={{ ...Y.notice, borderColor: "#fecaca", background: "#fff5f5", color: "#991b1b" }}>
          {loadError}
          <div style={{ marginTop: 10 }}>
            <button type="button" className="vas-btn" onClick={load}>
              <i className="fas fa-redo" />Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const contactDefaults = resolved?.contacts || {};

  const renderSwitchRow = (group, item, unsupportedReason = null) => {
    const on = !unsupportedReason && effective(group, item.key);
    const inherited = draft?.[group]?.[item.key] === null || draft?.[group]?.[item.key] === undefined;
    // The server reports where an unset switch gets its value: "legacy" is an
    // older toggle saved on the profile, "default" is the Lusuku default.
    const inheritedFrom = resolved?.sources?.[group]?.[item.key] === "legacy"
      ? "Not set here yet. Follows an older setting saved on your profile."
      : "Not set here yet. Follows the Lusuku default.";
    const prov = group === "channels" ? providerState(item.key) : null;
    return (
      <div key={item.key} className="vas-row" style={{ background: on ? item.tint : "#fff", opacity: unsupportedReason ? 0.72 : 1 }}>
        <div style={Y.iconBox(on, item.color)}>
          <i className={item.icon} style={Y.icon(on)} />
        </div>
        <div className="vas-row-body">
          <div className="vas-row-title">
            <span style={Y.title}>{item.label}{item.help}</span>
            {unsupportedReason ? (
              <span style={Y.chip("#b45309")}>not available yet</span>
            ) : inherited ? (
              <span style={Y.chip("#64748b")} title={inheritedFrom}>inherited</span>
            ) : (
              <>
                <span style={Y.chip(item.color)}>set here</span>
                <button type="button" className="vas-link" onClick={() => setPref(group, item.key, null)}>
                  use default
                </button>
              </>
            )}
          </div>
          <div style={Y.desc}>{item.desc}</div>
          {/* Same shape as the "provider not configured" note below a channel:
              the switch stays visible, and the line underneath says why it
              cannot be used. */}
          {unsupportedReason && (
            <div style={Y.provider(false)}>
              <i className="fas fa-exclamation-triangle" />
              <span>{unsupportedReason}</span>
            </div>
          )}
          {prov && (
            <div style={Y.provider(prov.ok)}>
              <i className={prov.ok ? "fas fa-plug" : "fas fa-exclamation-triangle"} />
              <span>{prov.via}</span>
            </div>
          )}
        </div>
        <Switch
          checked={on}
          disabled={!!unsupportedReason}
          onChange={(e) => setPref(group, item.key, e.target.checked)}
          color="primary"
          size="small"
          inputProps={{ "aria-label": `${item.label} alerts` }}
        />
      </div>
    );
  };

  return (
    <div className="vas-wrap">
      <div style={Y.head}>Alert settings<SettingHelp topic="vendorOrderAlerts" /></div>
      <p style={Y.sub}>
        How Lusuku reaches your business when something happens on your account. One set of choices
        for the whole team. Switches show what will actually happen, including anything inherited.
      </p>

      {/* Channels */}
      <div className="vas-section">
        <SectionHead icon="fas fa-broadcast-tower" color="#0a578b" title={<>Channels<SettingHelp topic="vendorAlertChannels" /></>} sub="Where alerts are delivered" />
        {CHANNELS.map((c) => renderSwitchRow("channels", c))}
      </div>

      {/* Contacts */}
      <div className="vas-section">
        <SectionHead icon="fas fa-address-card" color="#057a55" title={<>Alert contacts<SettingHelp topic="vendorAlertContacts" /></>} sub="Leave blank to use your profile contacts" />
        <div className="vas-contacts">
          <div>
            <label style={Y.label} htmlFor="vas-alert-email">
              <i className="fas fa-envelope" style={{ color: "#0a578b" }} />
              Alert email
              {draft.contacts.alertEmail ? (
                <button type="button" className="vas-link" style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }} onClick={() => setContact("alertEmail", null)}>
                  clear, use default
                </button>
              ) : null}
            </label>
            <input
              id="vas-alert-email"
              type="email"
              className={`vas-input${isFieldError(fieldError, "alertEmail") ? " is-invalid" : ""}`}
              value={draft.contacts.alertEmail || ""}
              placeholder={contactDefaults.email ? `Defaults to ${contactDefaults.email}` : "No default email on your profile"}
              onChange={(e) => setContact("alertEmail", e.target.value === "" ? null : e.target.value)}
              onKeyDown={swallowEnter}
              autoComplete="off"
            />
            {isFieldError(fieldError, "alertEmail") ? (
              <div style={Y.err}>{fieldError.message}</div>
            ) : (
              <div style={Y.help}>Alert emails go here instead of your profile address. Branch orders also reach that branch's email.</div>
            )}
          </div>
          <div>
            <label style={Y.label} htmlFor="vas-alert-phone">
              <i className="fas fa-mobile-alt" style={{ color: "#057a55" }} />
              Alert phone
              {draft.contacts.alertPhone ? (
                <button type="button" className="vas-link" style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }} onClick={() => setContact("alertPhone", null)}>
                  clear, use default
                </button>
              ) : null}
            </label>
            <input
              id="vas-alert-phone"
              type="tel"
              className={`vas-input${isFieldError(fieldError, "alertPhone") ? " is-invalid" : ""}`}
              value={draft.contacts.alertPhone || ""}
              placeholder={contactDefaults.phone ? `Defaults to ${contactDefaults.phone}` : "No default phone on your profile"}
              onChange={(e) => setContact("alertPhone", e.target.value === "" ? null : e.target.value)}
              onKeyDown={swallowEnter}
              autoComplete="off"
            />
            {isFieldError(fieldError, "alertPhone") ? (
              <div style={Y.err}>{fieldError.message}</div>
            ) : (
              <div style={Y.help}>SMS and WhatsApp alerts go here. {PHONE_HINT}</div>
            )}
          </div>
        </div>
      </div>

      {/* Events */}
      <div className="vas-section">
        <SectionHead icon="fas fa-bell" color="#f6961e" title="What to alert me about" sub="Each one goes out on every channel switched on above" />
        {EVENTS.map((ev) =>
          renderSwitchRow(
            "events",
            ev,
            supportedEvents.includes(ev.key) ? null : unsupportedEventReason(data?.vendorType, ev.key),
          ),
        )}
      </div>

      {/* Actions */}
      <div className="vas-actions">
        <button type="button" className="vas-btn vas-btn-primary" disabled={saving || !settingsGate.allowed} title={settingsGate.title} onClick={save}>
          <i className="fas fa-save" />
          {saving ? "Saving..." : "Save alert settings"}
        </button>
        <button type="button" className="vas-btn" disabled={testing || !settingsGate.allowed} onClick={sendTest} title={settingsGate.title || "Sends a test message on every channel that is switched on, using the saved settings"}>
          <i className="fas fa-paper-plane" style={{ color: "#0a578b" }} />
          {testing ? "Sending..." : "Send test alert"}
        </button>
        <SettingHelp topic="vendorAlertTest" />
        {dirty && (
          <span style={{ fontSize: "0.76rem", color: "#b45309", fontWeight: 600 }}>
            <i className="fas fa-circle" style={{ fontSize: 7, marginRight: 6, verticalAlign: "middle" }} />
            Unsaved changes. The test uses what is saved.
          </span>
        )}
      </div>

      {/* Test results */}
      {testResults && (
        <div className="vas-section">
          <SectionHead icon="fas fa-vial" color="#7e3af2" title="Test results" sub="Per channel" />
          {/* The test endpoint answers with all three channels every time, a
              switched-off one included, so this is not "nothing was switched
              on" (that reading was unreachable). It is the one case left: a
              response that carried no per-channel results at all. */}
          {testResults.length === 0 ? (
            <div style={Y.muted}>
              The server did not report a result for any channel. Try the test again.
            </div>
          ) : (
            <ul className="vas-results">
              {testResults.map((r, i) => (
                <li key={`${r.channel}-${i}`}>
                  <StatusBadge status={r.status} />
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>{CHANNEL_LABELS[r.channel] || r.channel}</span>
                    {r.to ? <span style={{ color: "#64748b" }}> to {r.to}</span> : null}
                    {providerLabel(r.provider) ? (
                      <span style={{ color: "#94a3b8" }}> via {providerLabel(r.provider)}</span>
                    ) : null}
                    {/* The server already writes a sentence per channel in
                        `summary` ("SMS skipped: twilio_not_configured."), which
                        this list used to throw away in favour of the bare tag. */}
                    {r.status !== "SENT" && (r.summary || r.reason) ? (
                      <div style={{ color: STATUS[r.status]?.color || "#64748b", marginTop: 2 }}>
                        {humanReason(r.summary || r.reason)}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Recent alerts */}
      <div className="vas-section">
        <SectionHead icon="fas fa-history" color="#64748b" title="Recent alerts" sub={recent.length ? `Last ${recent.length}` : null} />
        {recent.length === 0 ? (
          <div style={Y.muted}>No alerts have been sent to you yet.</div>
        ) : (
          <ul className="vas-results">
            {recent.slice(0, 20).map((r, i) => (
              <li key={`${r.createdAt || ""}-${i}`}>
                <StatusBadge status={r.status} />
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>{EVENT_LABELS[r.event] || r.event || "Alert"}</span>
                  {/* An EVENT-level skip is logged with `channel: null` (the
                      vendor switched the whole event off, or the order was over
                      the alert cap), and this row is exactly the one a vendor
                      reads when nothing arrived. It used to end "New order on"
                      with nothing after it. */}
                  {r.channel ? (
                    <span style={{ color: "#64748b" }}> on {CHANNEL_LABELS[r.channel] || r.channel}</span>
                  ) : null}
                  {r.to ? <span style={{ color: "#94a3b8" }}> to {r.to}</span> : null}
                  {r.status !== "SENT" && r.reason ? (
                    <div style={{ color: STATUS[r.status]?.color || "#64748b", marginTop: 2 }}>
                      {humanReason(r.reason)}
                    </div>
                  ) : null}
                </div>
                <span className="vas-recent-when" title={r.createdAt || ""}>{timeAgo(r.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
